import type React from "react";
import { useContext, useEffect, useRef, useState } from "react";

import { Accordion, AccordionSummary } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { getCareEventsSuggestedQueryKey } from "api/hooks/useGetCareEventsSuggested";
import { getPatientCareEventsQueryKey } from "api/hooks/useGetPatientCareEventsQuery";
import { usePostCareEventSuggested } from "api/hooks/usePostCareEventSuggested";
import { useMergeCareEvent } from "api/hooks/usePutPatientCareEventMutation";
import type { CareEvent } from "api/models/CareEvent";
import { useFeatureToggleContext } from "contexts/FeatureToggleContext";
import { useProfileContext } from "contexts/ProfileContext";
import { CommunicationGroupIcon } from "routes/dashboard/default/components/MedicalNotesTable/CommunicationGroupIcon";
import { CPTCode } from "routes/dashboard/default/components/MedicalNotesTable/CPTCode";
import { BaseButton } from "shared/atoms/BaseButton";
import { Hidden } from "shared/atoms/Hidden";
import { MUIUnifiedModal } from "shared/atoms/MUIUnifiedModal";
import type { ICDCodeResponse } from "types";
import { CurrentPatientContext } from "utils/contexts";
import type { UseGetPatientFullDetailsQueryData } from "utils/contexts/providers/useGetPatientsFullDetailsQuery";
import { Feature } from "utils/features/types";
import { useWindowSize } from "utils/hooks";
import { isKickOffNote } from "utils/medicalNotes";

import type { AutosaveStatus } from "../AutosaveIndicator";
import { AutosaveIndicator } from "../AutosaveIndicator";
import { DynamicMedicalNoteForm } from "../DynamicMedicalNoteForm";
import { MedicalNoteDate } from "../MedicalNoteDate";
import { MedicalNoteLabel } from "../MedicalNoteLabel";

import { FormContainer, MedicalNoteDateContainer, NoteTypeLabel, SignFirstBadge } from "./noteStyles";

export const MedicalNote: React.FC<CareEvent> = ({ ...careEvent }) => {
  const {
    id,
    communication_group,
    cpt_codes,
    message_interaction_id,
    label,
    patient_id,
    rtm_care_event_log_id,
    start_time,
    suggested,
    user_course_week_id,
    week_number,
  } = careEvent;
  const [open, setOpen] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState<AutosaveStatus>("idle");
  const theme = useTheme();
  const { profile } = useProfileContext();
  invariant(profile, "Profile context not set");
  const { hasFeature } = useFeatureToggleContext();
  const { activeCareEventNote, setActiveCareEventNote, patient } = useContext<{
    activeCareEventNote: number;
    setActiveCareEventNote: (id: number) => void;
    availableICDCodes: ICDCodeResponse[];
    patient?: UseGetPatientFullDetailsQueryData;
  }>(CurrentPatientContext);
  invariant(patient, "Patient context not set");
  const { width } = useWindowSize();
  const { t } = useTranslation();
  const { mergeCareEvent } = useMergeCareEvent({ label, patient_id });
  const createCareEventSuggested = usePostCareEventSuggested();

  const isEditingDateEnabled = hasFeature(Feature.EDIT_NOTE_DATE) && label.toUpperCase() !== "CHAT";

  const noteRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (activeCareEventNote) {
      const isCareEventSelected = activeCareEventNote === id;
      setOpen(isCareEventSelected);

      if (isCareEventSelected) {
        setTimeout(() => {
          if (noteRef.current !== null) {
            noteRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 500);
      }
    }
  }, [activeCareEventNote]);

  const onChangeDate = (date: string) => {
    mergeCareEvent({
      careEventId: id,
      data: { start_time: date },
      skipInvalidation: true,
    });
  };

  const startTime = start_time ?? "";

  const onClick = () => {
    if (suggested && !open && (message_interaction_id || rtm_care_event_log_id || user_course_week_id)) {
      createCareEventSuggested.mutateAsync(
        {
          data: {
            cpt_codes,
            label: careEvent.label,
            message_interaction_id,
            rtm_care_event_log_id,
            user_course_week_id,
          },
          patientID: patient.id,
        },
        {
          onSuccess(data) {
            const queryKey = getPatientCareEventsQueryKey({ patientId: patient.id, signed: false });
            const previousCareEvents = queryClient.getQueryData<CareEvent[]>(queryKey);
            if (previousCareEvents) {
              queryClient.setQueryData<CareEvent[]>(queryKey, [...previousCareEvents, data]);
            }
            queryClient.invalidateQueries(getCareEventsSuggestedQueryKey([patient.id]));
            setActiveCareEventNote(data.id);
          },
        }
      );
    }
    setOpen(prevState => !prevState);
  };

  return (
    <Accordion
      ref={noteRef}
      expanded={open}
      onChange={onClick}
      disableGutters
      sx={{
        background: open ? theme.colors.redesign.db1 : theme.colors.white,
        borderRadius: "8px",
        border: `1px solid ${theme.colors.redesign.db20}`,
        boxShadow: "none",
        "&::before": { display: "none" },
        transition: "background-color 250ms ease",
      }}
    >
      <AccordionSummary
        data-testid="medical_note"
        sx={{
          cursor: "pointer",
          "& .MuiAccordionSummary-content": { margin: 0 },
        }}
      >
        <NoteContent>
          <LeftSection>
            <NoteTypeLabel>
              {cpt_codes &&
                cpt_codes.length > 0 &&
                cpt_codes.map(cptCode => <CPTCode cptCode={cptCode} key={cptCode} />)}
              <CommunicationGroupIcon communicationGroup={communication_group} />
              <MedicalNoteLabel careEventLabel={label} weekNumber={week_number} />
            </NoteTypeLabel>
            {isKickOffNote(label) && <SignFirstBadge>{t("patients.notes.sign_first")}</SignFirstBadge>}
            <Hidden type="belowTablet">
              <AutosaveIndicator status={autosaveStatus} />
            </Hidden>
          </LeftSection>
          <CenterSection $editing={open}>
            <MedicalNoteDate
              defaultDate={startTime}
              onChange={onChangeDate}
              isEditingDateEnabled={isEditingDateEnabled}
              open={open && width > parseInt(theme.breakpoint, 10)}
            />
          </CenterSection>
          <RightSection data-testid="toggle_medical_note_form">
            <ChevronButton $isCollapsed={!open}>
              <BaseButton variant="action" icon="chevron-down" iconPlacement="end">
                <ButtonText>{open ? t("buttons.hide") : t("buttons.show")}</ButtonText>
              </BaseButton>
            </ChevronButton>
          </RightSection>
        </NoteContent>
      </AccordionSummary>

      <Hidden type="belowTablet">
        <FormContainer open={open}>
          <DynamicMedicalNoteForm
            careEvent={careEvent}
            open={open}
            setAutosaveStatus={setAutosaveStatus}
            suggested={suggested}
            startTime={startTime}
          />
        </FormContainer>
      </Hidden>

      <Hidden type="aboveTablet">
        <MUIUnifiedModal
          open={open}
          onClose={() => setOpen(false)}
          title={<MedicalNoteLabel careEventLabel={label} weekNumber={week_number} />}
          status={<AutosaveIndicator status={autosaveStatus} />}
        >
          {start_time && (
            <MedicalNoteDateContainer>
              <MedicalNoteDate
                defaultDate={startTime}
                onChange={onChangeDate}
                isEditingDateEnabled={isEditingDateEnabled}
                open
              />
            </MedicalNoteDateContainer>
          )}
          <DynamicMedicalNoteForm
            careEvent={careEvent}
            open={open}
            setAutosaveStatus={setAutosaveStatus}
            suggested={suggested}
            startTime={startTime}
          />
        </MUIUnifiedModal>
      </Hidden>
    </Accordion>
  );
};

const NoteContent = styled.div`
  display: grid;
  grid-template-columns: 3fr auto 2fr;
  align-items: center;
  width: 100%;
  padding: 16px 0;
  ${({ theme }) => theme.belowLargeBreakpoint} {
    grid-template-columns: 8fr auto 3fr;
  }
  ${({ theme }) => theme.belowBreakpoint} {
    padding: 12px;
    grid-template-columns: 1fr 1fr;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  ${({ theme }) => theme.belowBreakpoint} {
    display: block;
  }
`;

const CenterSection = styled.div<{ $editing: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme }) => theme.belowBreakpoint} {
    display: none;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const ChevronButton = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  svg {
    transition: transform 0.2s ease;
    transform: ${({ $isCollapsed }) => ($isCollapsed ? "rotateX(0deg)" : "rotateX(-180deg)")};
  }
`;

const ButtonText = styled.span`
  ${({ theme }) => theme.belowMobileBreakpoint} {
    display: none;
  }
`;
